/**
 * admin-frontend/src/pages/Overview.js
 *
 * FIX BUG-005: infinite loop no useEffect.
 *
 * Causa: useApi retorna `execute` via useCallback com deps [apiFunction, onSuccess, onError].
 * Se apiFunction for uma função importada (estável), execute é estável.
 * MAS o useEffect tinha 6 execute functions nas suas deps — qualquer re-render
 * do componente pai podia recriar alguma função e causar loop.
 *
 * Fix: chamar fetchData uma única vez no mount com uma flag.
 * Socket updates fazem refresh sem tocar nas deps do useEffect inicial.
 */

import React, { useEffect, useRef } from 'react';
import { useNavigate }              from 'react-router-dom';
import { useApi }                   from '../hooks/useApi';
import { useSocket }                from '../hooks/useSocket';
import { useToast }                 from '../hooks/useToast';
import StatCard                     from '../components/dashboard/StatCard';
import SaudeBackups from '../components/dashboard/SaudeBackups';
import ActivityFeed                 from '../components/dashboard/ActivityFeed';
import DataTable                    from '../components/common/DataTable';
import LoadingSpinner               from '../components/common/LoadingSpinner';
import { formatDateShort }          from '../constants'; // FIX: import fora de src/ quebra o build CRA
import {
  getDashboardStats,
  getRecentActivity,
  getOpenRequests,
} from '../utils/api';
import {
  Users, Briefcase, ClipboardList, DollarSign, Building2,
  Star, TrendingUp, AlertTriangle, CheckCircle, UserCheck,
  CalendarDays, ArrowUpRight, ShieldCheck,
} from 'lucide-react';

const Overview = () => {
  const navigate         = useNavigate();
  const { success }      = useToast();
  const { lastMessage }  = useSocket();
  const initialLoadDone  = useRef(false); // FIX: evita re-fetch desnecessário

  const { data: stats,     isLoading: statsLoading,    execute: fetchStats }     = useApi(getDashboardStats);
  const { data: activities, isLoading: activityLoading, execute: fetchActivities } = useApi(getRecentActivity);
  const { data: requests,  isLoading: requestsLoading, execute: fetchRequests }  = useApi(getOpenRequests);

  // FIX: carregar uma única vez no mount com ref guard
  useEffect(() => {
    if (initialLoadDone.current) return;
    initialLoadDone.current = true;
    fetchStats();
    fetchActivities();
    fetchRequests();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // ^ deps vazias intencionalmente: fetch só no mount

  // Socket: actualizar stats e actividade quando chega mensagem nova
  useEffect(() => {
    if (!lastMessage) return;
    fetchStats();
    fetchActivities();
    if (lastMessage.type === 'new_dispute') success('Nova mediação recebida!');
    if (lastMessage.type === 'new_request') success('Novo pedido recebido!');
  }, [lastMessage]); // eslint-disable-line react-hooks/exhaustive-deps

  const isLoading = statsLoading || activityLoading || requestsLoading;

  // Números reais vindos de /admin/stats. Sem inventar: se a API ainda não
  // respondeu mostramos "—", nunca um valor de exemplo.
  const n = (v) => (v === null || v === undefined ? null : Number(v));
  const show = (v) => (v === null ? '—' : v.toLocaleString('pt-PT'));

  const real = {
    totalUsers:       n(stats?.total_users),
    totalWorkers:     n(stats?.total_workers),
    openRequests:     n(stats?.open_requests),
    totalRevenue:     n(stats?.total_revenue),
    pendingApprovals: n(stats?.pending_workers),
    pendingBusinesses: n(stats?.pending_businesses),
    activeDisputes:   n(stats?.active_disputes),
    avgRating:        n(stats?.avg_rating),
    ratedReviews:     n(stats?.rated_reviews),
    totalRequests:    n(stats?.total_requests),
    completedRequests: n(stats?.completed_requests),
  };
  const completionRate = (real.totalRequests && real.totalRequests > 0)
    ? Math.round((real.completedRequests / real.totalRequests) * 100)
    : null;

  const requestColumns = [
    { key: 'request_id', label: 'ID', width: '80px' },
    { key: 'client_name',  label: 'Cliente' },
    { key: 'service_type', label: 'Serviço' },
    {
      key: 'status', label: 'Status', render: (row) => (
        <span className={`px-2 py-1 rounded-full text-xs font-medium
          ${row.status === 'open'     ? 'bg-yellow-500/20 text-yellow-400' :
            row.status === 'approved' ? 'bg-green-500/20 text-green-400'   :
            row.status === 'closed'   ? 'bg-gray-500/20 text-gray-400'     :
                                        'bg-blue-500/20 text-blue-400'}`}>
          {({ open: 'Aberta', pending: 'Pendente', awaiting_payment: 'Aguarda pagamento',
             approved: 'Aprovada', assigned: 'Atribuída', in_progress: 'Em andamento',
             completed: 'Completada', closed: 'Fechada', cancelled: 'Cancelada' }[row.status]) || row.status}
        </span>
      ),
    },
    {
      key: 'created_at', label: 'Data',
      render: (row) => formatDateShort(row.created_at), // FIX: usa formatador partilhado
    },
  ];

  if (isLoading && !stats) {
    return (
      <div className="flex items-center justify-center h-96">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <section className="ops-hero">
        <div className="ops-hero-copy">
          <span className="ops-eyebrow">Centro de operações</span>
          <h1>Visão geral da plataforma</h1>
          <p>Acompanhe a procura, a rede de profissionais e os casos que precisam de decisão.</p>
          <div className="ops-health"><span /><strong>Sistemas operacionais</strong><small>Dados atualizados em tempo real</small></div>
        </div>
        <div className="ops-hero-side">
          <div><CalendarDays /><span><small>Hoje</small><strong>{new Date().toLocaleDateString('pt-PT', { day: '2-digit', month: 'long', year: 'numeric' })}</strong></span></div>
          <button onClick={() => navigate('/admin/analytics')}>Abrir relatórios <ArrowUpRight /></button>
        </div>
      </section>

      {/* Só aparece quando um backup parou. Um sinal verde permanente
          deixa-se de ver ao fim de uma semana. */}
      <SaudeBackups />

      <div className="ops-priority-grid">
        <button onClick={() => navigate('/admin/verifications')}><span className="ops-priority-icon ops-priority-icon--gold"><UserCheck /></span><span><small>Verificações pendentes</small><strong>{show(real.pendingApprovals)}</strong></span><ArrowUpRight /></button>
        <button onClick={() => navigate('/admin/disputes')}><span className="ops-priority-icon ops-priority-icon--red"><AlertTriangle /></span><span><small>Mediações ativas</small><strong>{show(real.activeDisputes)}</strong></span><ArrowUpRight /></button>
        {/* Empresas por confirmar.
            A página existia e estava no menu, mas o número não aparecia em
            lado nenhum: quem criava a empresa ficava a aguardar e a gestão só
            dava por ela se fosse à lista de propósito. Uma fila de trabalho
            que é preciso procurar é uma fila que fica por fazer. */}
        <button onClick={() => navigate('/admin/businesses')}><span className="ops-priority-icon ops-priority-icon--gold"><Building2 /></span><span><small>Empresas por confirmar</small><strong>{show(real.pendingBusinesses)}</strong></span><ArrowUpRight /></button>
        <button onClick={() => navigate('/admin/requests')}><span className="ops-priority-icon ops-priority-icon--blue"><ShieldCheck /></span><span><small>Taxa de conclusão</small><strong>{completionRate === null ? '—' : `${completionRate}%`}</strong></span><ArrowUpRight /></button>
      </div>

      {/* Stats primárias */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard title="Total Utilizadores"   value={show(real.totalUsers)}   subtitle="Clientes e profissionais"  icon={Users}         color="blue"   onClick={() => navigate('/admin/users')} />
        <StatCard title="Profissionais"        value={show(real.totalWorkers)} subtitle="Registados na plataforma"  icon={Briefcase}     color="teal"   onClick={() => navigate('/admin/workers')} />
        <StatCard title="Pedidos abertos" value={show(real.openRequests)} subtitle="Ainda por concluir"        icon={ClipboardList} color="orange" onClick={() => navigate('/admin/requests')} />
        <StatCard title="Volume Libertado"     value={real.totalRevenue === null ? '—' : `Kz ${real.totalRevenue.toLocaleString('pt-PT')}`} subtitle="Valor protegido já libertado aos profissionais" icon={DollarSign} color="green" onClick={() => navigate('/admin/payments')} />
      </div>

      <div className="ops-section-label"><span>Indicadores de qualidade</span><small>Desempenho e confiança da plataforma</small></div>
      {/* Stats secundárias */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard title="Aprovações Pendentes" value={show(real.pendingApprovals)} subtitle="Profissionais para verificar" icon={UserCheck}     color="purple" onClick={() => navigate('/admin/verifications')} />
        <StatCard title="Mediações ativas"      value={show(real.activeDisputes)}   subtitle="Requerem atenção"             icon={AlertTriangle} color="red"    onClick={() => navigate('/admin/disputes')} />
        <StatCard title="Empresas por Confirmar" value={show(real.pendingBusinesses)} subtitle="Aguardam decisão da Dozy"   icon={Building2}     color="orange" onClick={() => navigate('/admin/businesses')} />
        <StatCard title="Avaliação Média"      value={real.ratedReviews ? real.avgRating.toFixed(1) : '—'} subtitle={real.ratedReviews ? `De 5 estrelas · ${real.ratedReviews} avaliações` : 'Ainda sem avaliações válidas'} icon={Star} color="cyan" onClick={() => navigate('/admin/reviews')} />
        <StatCard title="Taxa de Conclusão"    value={completionRate === null ? '—' : `${completionRate}%`} subtitle={real.totalRequests ? `${real.completedRequests} de ${real.totalRequests} pedidos` : 'Ainda sem pedidos'} icon={CheckCircle} color="cyan" onClick={() => navigate('/admin/requests')} />
      </div>

      {/* Tabela + Feed */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <DataTable
            title="Pedidos recentes"
            data={(requests || []).slice(0, 5)}
            columns={requestColumns}
            pageSize={5}
            pagination={false}
            onRowClick={(row) => navigate(`/admin/requests?id=${row.request_id}`)}
            actions={
              <button onClick={() => navigate('/admin/requests')} className="text-sm text-teal-400 hover:text-teal-300 font-medium">
                Ver todas →
              </button>
            }
          />
        </div>
        <div className="lg:col-span-1">
          <ActivityFeed activities={activities || []} />
        </div>
      </div>

      {/* Ações rápidas */}
      <div className="ops-quick-actions bg-gray-800 rounded-xl border border-gray-700 p-6">
        <div className="ops-section-label"><span>Ações rápidas</span><small>Atalhos para as tarefas mais frequentes</small></div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: 'Aprovar profissional', icon: UserCheck,    color: 'bg-green-500/10 text-green-400', action: () => navigate('/admin/workers?tab=pending') },
            { label: 'Novo pedido',    icon: ClipboardList, color: 'bg-blue-500/10 text-blue-400',   action: () => navigate('/admin/requests')            },
            { label: 'Resolver mediação',    icon: AlertTriangle, color: 'bg-red-500/10 text-red-400',     action: () => navigate('/admin/disputes')            },
            { label: 'Ver estatísticas',       icon: TrendingUp,    color: 'bg-purple-500/10 text-purple-400', action: () => navigate('/admin/analytics')         },
          ].map(({ label, icon: Icon, color, action }, idx) => (
            <button key={idx} onClick={action}
              className="flex flex-col items-center gap-3 p-4 rounded-xl bg-gray-700/50 hover:bg-gray-700 transition-all duration-200 group">
              <div className={`p-3 rounded-xl ${color} group-hover:scale-110 transition-transform`}>
                <Icon className="w-6 h-6" />
              </div>
              <span className="text-sm text-gray-300 font-medium">{label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Overview;
